import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, MousePointer, DollarSign, LinkIcon, Plus, Copy, ExternalLink, Zap } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinLoading, setJoinLoading] = useState("");
  const [copied, setCopied] = useState("");

  const token = localStorage.getItem("ctcsb_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, programsRes] = await Promise.all([
          axios.get(`${API}/affiliate/stats`, { headers, withCredentials: true }),
          axios.get(`${API}/affiliate/programs`, { withCredentials: true }),
        ]);
        setStats(statsRes.data);
        setPrograms(programsRes.data);
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const joinProgram = async (programId) => {
    setJoinLoading(programId);
    try {
      await axios.post(`${API}/affiliate/join`, { program_id: programId }, { headers, withCredentials: true });
      const statsRes = await axios.get(`${API}/affiliate/stats`, { headers, withCredentials: true });
      setStats(statsRes.data);
    } catch (err) {
      console.error("Join error:", err);
    } finally {
      setJoinLoading("");
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(""), 2000);
  };

  const chartData = stats?.links?.map((l) => ({
    name: l.program_name?.split(" ").slice(0, 2).join(" ") || "Link",
    clicks: l.clicks || 0,
    conversions: l.conversions || 0,
  })) || [];

  const statCards = [
    { icon: LinkIcon, label: "ACTIVE LINKS", value: stats?.total_links || 0, color: "#E31937" },
    { icon: MousePointer, label: "TOTAL CLICKS", value: stats?.total_clicks || 0, color: "#00BFFF" },
    { icon: TrendingUp, label: "CONVERSIONS", value: stats?.total_conversions || 0, color: "#22C55E" },
    { icon: DollarSign, label: "EARNINGS", value: `$${(stats?.total_earnings || 0).toFixed(2)}`, color: "#F59E0B" },
  ];

  const joinedProgramIds = new Set((stats?.links || []).map((l) => l.program_id));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0C' }}>
        <div className="w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="min-h-screen pt-20 pb-12 px-6 md:px-12" style={{ background: '#0A0A0C' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <span className="text-xs tracking-[0.2em] uppercase text-[#E31937] font-semibold">COMMAND CENTER</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {user?.name || "Affiliate"}
          </h1>
          {user?.interests?.length === 0 && (
            <Link to="/interests" className="inline-flex items-center gap-1 mt-3 text-sm text-[#00BFFF] hover:text-[#33CCFF]">
              <Zap className="w-4 h-4" /> Set your interests to get personalized programs
            </Link>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#2A2A30] mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#141417] border border-[#2A2A30] p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2 mb-3">
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <span className="text-xs tracking-[0.15em] uppercase text-slate-400 font-semibold">{s.label}</span>
              </div>
              <div className="text-4xl font-bold tracking-tighter text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="links" className="w-full">
          <TabsList className="bg-[#141417] border border-[#2A2A30] rounded-none p-1 mb-8">
            <TabsTrigger value="links" data-testid="tab-my-links" className="rounded-none data-[state=active]:bg-[#E31937] data-[state=active]:text-white text-slate-400 uppercase text-xs tracking-wider font-semibold px-6">
              My Links
            </TabsTrigger>
            <TabsTrigger value="programs" data-testid="tab-programs" className="rounded-none data-[state=active]:bg-[#E31937] data-[state=active]:text-white text-slate-400 uppercase text-xs tracking-wider font-semibold px-6">
              Programs
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-none data-[state=active]:bg-[#E31937] data-[state=active]:text-white text-slate-400 uppercase text-xs tracking-wider font-semibold px-6">
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* My Links */}
          <TabsContent value="links">
            {stats?.links?.length === 0 ? (
              <div className="bg-[#141417] border border-[#2A2A30] p-12 text-center">
                <LinkIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Affiliate Links Yet</h3>
                <p className="text-sm text-slate-400 mb-6">Join a program to get started with your affiliate links</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(stats?.links || []).map((link) => (
                  <div key={link.id} className="bg-[#141417] border border-[#2A2A30] p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`affiliate-link-${link.program_id}`}>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white text-sm">{link.program_name}</h4>
                      <p className="text-xs text-slate-500 font-mono truncate mt-1">{link.affiliate_url}</p>
                    </div>
                    <div className="flex items-center gap-6 text-center">
                      <div>
                        <div className="text-lg font-bold font-mono text-white">{link.clicks}</div>
                        <div className="text-xs text-slate-500 uppercase">Clicks</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold font-mono text-[#22C55E]">{link.conversions}</div>
                        <div className="text-xs text-slate-500 uppercase">Conv</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold font-mono text-[#F59E0B]">${(link.earnings || 0).toFixed(2)}</div>
                        <div className="text-xs text-slate-500 uppercase">Earned</div>
                      </div>
                      <button
                        onClick={() => copyLink(link.affiliate_url)}
                        data-testid={`copy-link-${link.program_id}`}
                        className="p-2 border border-[#2A2A30] hover:border-[#E31937] hover:bg-[#E31937]/10 transition-colors"
                      >
                        <Copy className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {copied && (
              <div className="fixed bottom-6 right-6 bg-[#22C55E] text-white px-4 py-2 text-sm font-semibold z-50">Link copied!</div>
            )}
          </TabsContent>

          {/* Programs */}
          <TabsContent value="programs">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#2A2A30]">
              {programs.map((p) => (
                <div key={p.id} className="bg-[#141417] border border-[#2A2A30] p-6 flex flex-col" data-testid={`program-card-${p.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#1C1C20] flex items-center justify-center overflow-hidden">
                      <img src={p.logo} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white">{p.name}</h3>
                      <span className="text-xs text-slate-500 uppercase">{p.category}</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-400 mb-4 flex-1">{p.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono text-[#22C55E]">
                      {p.commission_rate > 0 ? `${p.commission_rate}% commission` : p.reward_type || "Rewards"}
                    </span>
                    {joinedProgramIds.has(p.id) ? (
                      <span className="text-xs text-slate-500 uppercase font-semibold">Joined</span>
                    ) : (
                      <button
                        onClick={() => joinProgram(p.id)}
                        disabled={joinLoading === p.id}
                        data-testid={`join-program-${p.id}`}
                        className="flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase bg-[#E31937] text-white hover:bg-[#FF3B30] transition-colors disabled:opacity-50"
                      >
                        <Plus className="w-3 h-3" /> Join
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics">
            {chartData.length > 0 ? (
              <div className="bg-[#141417] border border-[#2A2A30] p-6">
                <h3 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Performance Overview</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2A30" />
                    <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ background: '#141417', border: '1px solid #2A2A30', color: '#fff' }}
                      labelStyle={{ color: '#94A3B8' }}
                    />
                    <Bar dataKey="clicks" fill="#00BFFF" />
                    <Bar dataKey="conversions" fill="#22C55E" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="bg-[#141417] border border-[#2A2A30] p-12 text-center">
                <TrendingUp className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Data Yet</h3>
                <p className="text-sm text-slate-400">Join programs and share your links to see analytics</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
